import { useCallback, useEffect, useReducer, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from 'react';
import type Hls from 'hls.js';
import type { QualityChoice } from '../lib/quality.ts';
import { AirPlayIcon, CheckIcon, ChevronIcon, CloseIcon, FullscreenIcon, PauseIcon, PipIcon, PlayIcon, ReplayIcon, SettingsIcon, SoundIcon, VolumeIcon } from './Icons.tsx';

/** What the controls need from whatever is playing the video: hls.js, or Safari's own HLS. */
export interface PlayerEngine {
  hls: Hls | null;
  /** Seconds behind where a live viewer normally sits: 0 at the live edge, null until known. */
  behindLive(): number | null;
  /** Back to the live edge, playing. */
  goLive(): void;
}

/** The viewer's Auto / High / Low choice (lib/quality.ts) and which quality is on screen. */
export interface QualityState {
  choice: QualityChoice;
  playing: 'hi' | 'lo' | null;
}

type Flash = { id: number; icon: ReactNode; side: 'left' | 'center' | 'right'; text?: string };
type MenuView = 'main' | 'quality' | 'speed';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
/** Behind live by more than this (seconds) and the LIVE mark turns into "Back to live". */
const BEHIND_LIVE = 4;
const HIDE_AFTER_MS = 2600;
const MEDIA_EVENTS = ['play', 'pause', 'playing', 'waiting', 'seeking', 'seeked', 'timeupdate', 'durationchange', 'progress', 'volumechange', 'ratechange', 'loadedmetadata', 'resize', 'ended', 'emptied', 'enterpictureinpicture', 'leavepictureinpicture', 'webkitpresentationmodechanged'];

type FullscreenDoc = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void; webkitFullscreenEnabled?: boolean };
type FullscreenEl = HTMLElement & { webkitRequestFullscreen?: () => void };
type AppleVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitSupportsFullscreen?: boolean;
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
  webkitShowPlaybackTargetPicker?: () => void;
};

const fullscreenElement = () => document.fullscreenElement ?? (document as FullscreenDoc).webkitFullscreenElement ?? null;

/** 0:42, 12:05, 1:02:09. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

const bufferedAhead = (el: HTMLVideoElement) => {
  for (let i = 0; i < el.buffered.length; i++) {
    if (el.buffered.start(i) <= el.currentTime + 0.1 && el.buffered.end(i) >= el.currentTime) return el.buffered.end(i) - el.currentTime;
  }
  return 0;
};

/**
 * ZIP2LIVE's own controls over the <video>: play, sound, LIVE / back to live or the replay's seek
 * bar, settings (quality, speed, stats), picture in picture, AirPlay and full screen. They hide
 * while the video plays untouched and come back on any mouse move, tap or key.
 *
 * Mouse: click plays or pauses, double-click goes full screen. Touch: a tap shows or hides the
 * controls, a double tap on either side of a replay skips 10 s. Keys (when the page has one
 * player, or focus is in it): space/k, m, f, i, ←/→ 5 s, j/l 10 s, ↑/↓ volume, 0-9.
 */
export function PlayerControls({
  video,
  root,
  engine,
  live,
  immersive,
  bottomInset = 0,
  title,
  badges,
  quality,
  onQuality,
  loading,
  problem,
  autoplayMuted,
  onUnmute,
}: {
  video: RefObject<HTMLVideoElement | null>;
  /** The element that goes full screen (the player's box, so badges and reactions come along). */
  root: RefObject<HTMLDivElement | null>;
  engine: RefObject<PlayerEngine>;
  live: boolean;
  immersive: boolean;
  /** Upright-phone layout: height of the page's bar over the bottom of the picture. */
  bottomInset?: number;
  /** Shown along the top in full screen. */
  title?: string;
  /** LIVE / REPLAY badge and viewer count, top left. */
  badges?: ReactNode;
  /** Null when the stream has one quality (or Safari's own player, which can't be told). */
  quality: QualityState | null;
  onQuality: (c: QualityChoice) => void;
  /** Before the first picture. */
  loading: boolean;
  problem: string | null;
  /** Autoplay with sound was refused, so it plays muted until a tap. */
  autoplayMuted: boolean;
  onUnmute: () => void;
}) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [active, setActive] = useState(true);
  const [menu, setMenu] = useState<MenuView | null>(null);
  const [stats, setStats] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [overControls, setOverControls] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [airplay, setAirplay] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastPointer = useRef<string>('mouse');
  const lastTap = useRef<{ at: number; side: Flash['side']; count: number }>({ at: 0, side: 'center', count: 0 });
  const flashId = useRef(0);
  const el = video.current;

  const poke = useCallback(() => {
    setActive(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setActive(false), HIDE_AFTER_MS);
  }, []);
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  // Re-render on everything the video reports, plus once a second for live position and stats.
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const onWaiting = () => {
      clearTimeout(stallTimer);
      // Only a pause in the picture worth showing: brief waits at a segment edge don't flash.
      stallTimer = setTimeout(() => setStalled(!v.paused && v.readyState < 3), 350);
    };
    const onFlowing = () => {
      clearTimeout(stallTimer);
      setStalled(false);
    };
    for (const e of MEDIA_EVENTS) v.addEventListener(e, rerender);
    v.addEventListener('play', poke);
    v.addEventListener('waiting', onWaiting);
    for (const e of ['playing', 'pause', 'seeked', 'canplay']) v.addEventListener(e, onFlowing);
    document.addEventListener('fullscreenchange', rerender);
    document.addEventListener('webkitfullscreenchange', rerender);
    const tick = setInterval(rerender, 1000);
    // AirPlay: Safari says whether a receiver is around.
    const onTargets = (e: Event) => setAirplay((e as Event & { availability?: string }).availability === 'available');
    const airplayEvents = 'WebKitPlaybackTargetAvailabilityEvent' in window;
    if (airplayEvents) v.addEventListener('webkitplaybacktargetavailabilitychanged', onTargets);
    return () => {
      clearTimeout(stallTimer);
      clearInterval(tick);
      for (const e of MEDIA_EVENTS) v.removeEventListener(e, rerender);
      v.removeEventListener('play', poke);
      v.removeEventListener('waiting', onWaiting);
      for (const e of ['playing', 'pause', 'seeked', 'canplay']) v.removeEventListener(e, onFlowing);
      document.removeEventListener('fullscreenchange', rerender);
      document.removeEventListener('webkitfullscreenchange', rerender);
      if (airplayEvents) v.removeEventListener('webkitplaybacktargetavailabilitychanged', onTargets);
    };
  }, [poke, video]);

  const showFlash = useCallback((f: Omit<Flash, 'id'>) => setFlash({ ...f, id: ++flashId.current }), []);

  const togglePlay = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.ended) v.currentTime = 0;
    if (v.paused || v.ended) {
      // A live stream paused for a while picks up at live, not where it stopped.
      if (live && (engine.current.behindLive() ?? 0) > 20) engine.current.goLive();
      else void v.play().catch(() => undefined);
    } else v.pause();
  }, [engine, live, video]);

  const seekBy = useCallback(
    (seconds: number, side: Flash['side'] = 'center', text?: string) => {
      const v = video.current;
      if (!v || live || !Number.isFinite(v.duration)) return;
      v.currentTime = Math.min(Math.max(0, v.currentTime + seconds), v.duration - 0.1);
      showFlash({ side, icon: null, text: text ?? `${seconds > 0 ? '+' : '−'}${Math.abs(seconds)} s` });
    },
    [live, showFlash, video],
  );

  const toggleMute = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.muted || v.volume === 0) {
      v.muted = false;
      if (v.volume === 0) v.volume = 0.5;
      onUnmute();
    } else v.muted = true;
  }, [onUnmute, video]);

  const canFullscreen = Boolean(
    ((document as FullscreenDoc).fullscreenEnabled || (document as FullscreenDoc).webkitFullscreenEnabled) ||
      (el as AppleVideo | null)?.webkitSupportsFullscreen,
  );
  const fullscreen = Boolean(root.current && fullscreenElement() === root.current);
  const toggleFullscreen = useCallback(() => {
    const box = root.current as FullscreenEl | null;
    const v = video.current as AppleVideo | null;
    if (!box || !v) return;
    const doc = document as FullscreenDoc;
    if (fullscreenElement()) {
      void (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      return;
    }
    if (doc.fullscreenEnabled && box.requestFullscreen) {
      box.requestFullscreen({ navigationUI: 'hide' }).then(
        () => {
          // Phones turn a landscape video sideways to fill the screen (Android; iPhones go below).
          const orientation = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
          if (matchMedia('(pointer: coarse)').matches && v.videoWidth > v.videoHeight) orientation.lock?.('landscape').catch(() => undefined);
        },
        () => undefined,
      );
    } else if (doc.webkitFullscreenEnabled && box.webkitRequestFullscreen) box.webkitRequestFullscreen();
    // iPhone: only the video itself can go full screen, with Apple's own controls.
    else v.webkitEnterFullscreen?.();
  }, [root, video]);

  const pipSupported = Boolean(
    el && ((document.pictureInPictureEnabled && !el.disablePictureInPicture) || (el as AppleVideo).webkitSupportsPresentationMode?.('picture-in-picture')),
  );
  const inPip = Boolean(el && (document.pictureInPictureElement === el || (el as AppleVideo).webkitPresentationMode === 'picture-in-picture'));
  const togglePip = useCallback(() => {
    const v = video.current as AppleVideo | null;
    if (!v) return;
    if (document.pictureInPictureElement === v) void document.exitPictureInPicture().catch(() => undefined);
    else if (document.pictureInPictureEnabled && v.requestPictureInPicture) void v.requestPictureInPicture().catch(() => undefined);
    else v.webkitSetPresentationMode?.(v.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture');
  }, [video]);

  // Keyboard shortcuts: inside the player, or anywhere on a page with just this one player.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target instanceof HTMLElement ? e.target : null;
      const inside = Boolean(t && root.current?.contains(t));
      const onPage = !t || t === document.body || t === document.documentElement;
      if (!inside && !(onPage && document.querySelectorAll('[data-player]').length === 1 && !document.querySelector('[aria-modal="true"]'))) return;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if ((e.key === ' ' || e.key === 'Enter') && t?.closest('button')) return;
      const v = video.current;
      if (!v) return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      let handled = true;
      if (k === ' ' || k === 'k') {
        showFlash({ side: 'center', icon: v.paused || v.ended ? <PlayIcon size={34} /> : <PauseIcon size={34} /> });
        togglePlay();
      } else if (k === 'm') toggleMute();
      else if (k === 'f') toggleFullscreen();
      else if (k === 'i' && pipSupported) togglePip();
      else if (k === 'Escape' && menu) setMenu(null);
      else if (!live && (k === 'ArrowLeft' || k === 'ArrowRight')) seekBy(k === 'ArrowLeft' ? -5 : 5);
      else if (!live && (k === 'j' || k === 'l')) seekBy(k === 'j' ? -10 : 10);
      else if (inside && (k === 'ArrowUp' || k === 'ArrowDown')) {
        v.muted = false;
        v.volume = Math.min(1, Math.max(0, Math.round((v.volume + (k === 'ArrowUp' ? 0.1 : -0.1)) * 10) / 10));
        showFlash({ side: 'center', icon: <VolumeIcon level={v.volume === 0 ? 'off' : v.volume < 0.5 ? 'low' : 'high'} />, text: `${Math.round(v.volume * 100)}%` });
      } else if (!live && /^[0-9]$/.test(k) && Number.isFinite(v.duration)) v.currentTime = (Number(k) / 10) * v.duration;
      else handled = false;
      if (handled) {
        e.preventDefault();
        poke();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [live, menu, pipSupported, poke, root, seekBy, showFlash, toggleFullscreen, toggleMute, togglePip, togglePlay, video]);

  if (!el) return null;

  const paused = el.paused;
  const ended = el.ended;
  const duration = Number.isFinite(el.duration) ? el.duration : 0;
  const behind = live ? engine.current.behindLive() : null;
  const atLive = behind === null || behind < BEHIND_LIVE;
  const muted = el.muted || el.volume === 0;
  const busy = loading || stalled;
  const shown = active || paused || ended || Boolean(menu) || scrubbing || overControls || busy;
  // Picture size by its short side, so an upright 720×1280 phone stream counts as 720p.
  const lines = Math.min(el.videoWidth, el.videoHeight);
  const hd = lines >= 720;
  const touchShown = lastPointer.current !== 'mouse' && shown;
  const bigButton = !busy && (paused || ended || touchShown);

  // Taps and clicks on the picture (not on the controls).
  const onSurfaceClick = (e: MouseEvent<HTMLDivElement>) => {
    if (menu) {
      setMenu(null);
      return;
    }
    if (lastPointer.current === 'mouse') {
      togglePlay();
      poke();
      return;
    }
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - box.left) / box.width;
    const side: Flash['side'] = x < 0.35 ? 'left' : x > 0.65 ? 'right' : 'center';
    const now = Date.now();
    const prev = lastTap.current;
    if (!live && side !== 'center' && now - prev.at < 320 && prev.side === side) {
      // Double tap (and each tap after it): skip 10 s more.
      const count = prev.count + 1;
      lastTap.current = { at: now, side, count };
      seekBy(side === 'left' ? -10 : 10, side, `${side === 'left' ? '−' : '+'}${count * 10} s`);
      poke();
      return;
    }
    lastTap.current = { at: now, side, count: 0 };
    if (shown && !paused) {
      clearTimeout(hideTimer.current);
      setActive(false);
    } else poke();
  };

  const menuPanel = menu && (
    <SettingsMenu
      view={menu}
      setView={setMenu}
      quality={quality}
      onScreen={lines}
      onQuality={(c) => {
        onQuality(c);
        setMenu(null);
        poke();
      }}
      rate={live ? null : el.playbackRate}
      onRate={(r) => {
        el.playbackRate = r;
        setMenu(null);
        poke();
      }}
      stats={stats}
      onStats={() => {
        setStats(!stats);
        setMenu(null);
      }}
      maxHeight={Math.max(140, (root.current?.clientHeight ?? 400) - (immersive ? 140 : 76))}
      className={immersive ? 'top-0 right-full mr-2' : 'right-0 bottom-full mb-3'}
    />
  );

  const settingsButton = (
    <CtrlButton label="Settings" onClick={() => setMenu(menu ? null : 'main')} pressed={Boolean(menu)} round={immersive} tip={!immersive}>
      <SettingsIcon />
      {hd && <span className="absolute top-1 right-0.5 rounded-[3px] bg-race px-[3px] text-[9px] leading-[13px] font-black">HD</span>}
    </CtrlButton>
  );

  const livePill = live && (
    atLive ? (
      <span className="speed-text inline-flex h-10 items-center gap-1.5 px-2 text-[13px]" title="You’re watching live">
        <span className="live-dot h-2 w-2 rounded-full bg-race" aria-hidden />
        Live
      </span>
    ) : (
      <button
        type="button"
        onClick={() => {
          engine.current.goLive();
          poke();
        }}
        className="btn-race mx-1 h-8 gap-1.5 px-3 text-[12px]"
        title={behind ? `${Math.round(behind)} s behind` : undefined}
      >
        <span className="h-2 w-2 rounded-full bg-white/60" aria-hidden />
        Back to live
      </button>
    )
  );

  return (
    <div
      className={`absolute inset-0 select-none ${fullscreen && !shown ? 'cursor-none' : ''}`}
      data-controls={shown ? 'shown' : 'hidden'}
      onPointerDownCapture={(e) => (lastPointer.current = e.pointerType)}
      onPointerMove={(e) => {
        if (e.pointerType === 'mouse') poke();
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse' && !paused && !menu) {
          clearTimeout(hideTimer.current);
          hideTimer.current = setTimeout(() => setActive(false), 500);
        }
      }}
    >
      {/* The picture: tap, click and double-click target. */}
      <div
        className="absolute inset-0 touch-manipulation"
        data-testid="player-surface"
        onClick={onSurfaceClick}
        onDoubleClick={() => {
          if (lastPointer.current === 'mouse' && !immersive) toggleFullscreen();
        }}
      />

      {/* Top: badges, and in full screen the title and wordmark. */}
      {(badges || fullscreen) && !immersive && (
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 p-3 sm:p-4">
          {badges}
          {fullscreen && (
            <div className={`flex min-w-0 flex-1 items-center justify-between gap-4 transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}>
              <p className="min-w-0 truncate text-lg font-extrabold drop-shadow">{title}</p>
              <span className="speed-text shrink-0 text-xl drop-shadow">
                ZIP<span className="text-race-300">2</span>LIVE
              </span>
            </div>
          )}
        </div>
      )}

      {/* Middle: loading stripes, the big play button, and what a key or double tap just did. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {busy && <span className="zip-loader" role="status" aria-label="Loading video" />}
        {bigButton && (
          <button
            type="button"
            aria-label={ended ? 'Watch again' : paused ? 'Play' : 'Pause'}
            onClick={() => {
              togglePlay();
              poke();
            }}
            className={`btn-race pointer-events-auto h-16 w-[84px] text-white sm:h-[72px] sm:w-24 ${paused || ended ? 'shadow-race-deep' : '!bg-navy-900/70 hover:!bg-navy-900/85'}`}
          >
            {ended ? <ReplayIcon size={34} /> : paused ? <PlayIcon size={34} /> : <PauseIcon size={34} />}
          </button>
        )}
      </div>
      {flash && <FlashMark key={flash.id} flash={flash} onDone={() => setFlash(null)} />}

      {stats && <StatsPanel video={el} engine={engine.current} quality={quality} live={live} onClose={() => setStats(false)} className={immersive ? 'top-[max(8rem,calc(env(safe-area-inset-top)+7rem))]' : badges ? 'top-14 sm:top-16' : 'top-3'} />}

      {autoplayMuted && (
        <button
          type="button"
          className={`absolute inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-extrabold text-navy shadow-lg ${immersive ? 'top-[max(4.5rem,calc(env(safe-area-inset-top)+3.75rem))] left-3' : 'top-3 right-3 sm:top-4 sm:right-4'}`}
          onClick={() => {
            el.muted = false;
            void el.play().catch(() => undefined);
            onUnmute();
          }}
        >
          <SoundIcon />
          Tap to unmute
        </button>
      )}

      {problem && (
        <div className="pointer-events-none absolute inset-x-0 bottom-20 flex justify-center px-4">
          <span className="rounded-full bg-navy-900/85 px-4 py-2 text-sm font-bold text-white">{problem}</span>
        </div>
      )}

      {immersive ? (
        // Phone held upright on a vertical stream: the page's own bars take the top and bottom, so
        // the controls stack on the right, under the top bar, and a replay's seek bar sits just
        // above the page's bottom bar (clear of the like buttons on the right).
        <>
          {!live && duration > 0 && (
            <div
              className={`absolute right-[4.5rem] left-3 flex items-center rounded-xl bg-navy-900/75 pr-3 backdrop-blur-md transition-opacity duration-200 ${shown ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
              style={{ bottom: bottomInset + 10 }}
            >
              <CtrlButton label={ended ? 'Watch again' : paused ? 'Play' : 'Pause'} onClick={togglePlay} tip={false}>
                {ended ? <ReplayIcon size={20} /> : paused ? <PlayIcon size={20} /> : <PauseIcon size={20} />}
              </CtrlButton>
              <div className="min-w-0 flex-1">
                <SeekBar video={el} duration={duration} onScrub={setScrubbing} />
              </div>
              <span className="text-xs font-bold whitespace-nowrap text-white/90 tabular-nums">
                {clock(el.currentTime)}
                <span className="text-white/50"> / {clock(duration)}</span>
              </span>
            </div>
          )}
          <div
            className={`absolute right-3 top-[max(4.5rem,calc(env(safe-area-inset-top)+3.75rem))] flex flex-col items-end gap-2 transition-opacity duration-200 ${shown ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            onPointerEnter={(e) => e.pointerType === 'mouse' && setOverControls(true)}
            onPointerLeave={() => setOverControls(false)}
          >
            {live && !atLive && livePill}
            <CtrlButton label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute} round tip={false}>
              <VolumeIcon level={muted ? 'off' : 'high'} />
            </CtrlButton>
            <div className="relative">
              {settingsButton}
              {menuPanel}
            </div>
            {pipSupported && (
              <CtrlButton label="Picture in picture" onClick={togglePip} pressed={inPip} round tip={false}>
                <PipIcon />
              </CtrlButton>
            )}
          </div>
        </>
      ) : (
        <div
          className={`absolute inset-x-0 bottom-0 p-2 transition duration-200 sm:p-3 ${shown ? 'opacity-100' : 'pointer-events-none translate-y-1 opacity-0'}`}
          onPointerEnter={(e) => e.pointerType === 'mouse' && setOverControls(true)}
          onPointerLeave={() => setOverControls(false)}
        >
          <div className="relative rounded-xl bg-navy-900/80 px-1 pb-0.5 ring-1 ring-white/10 backdrop-blur-md sm:px-1.5">
            {!live && duration > 0 && <SeekBar video={el} duration={duration} onScrub={setScrubbing} />}
            <div className={`flex items-center gap-0.5 ${!live && duration > 0 ? '' : 'pt-0.5'}`}>
              <CtrlButton label={ended ? 'Watch again' : paused ? 'Play (k)' : 'Pause (k)'} onClick={togglePlay}>
                {ended ? <ReplayIcon /> : paused ? <PlayIcon /> : <PauseIcon />}
              </CtrlButton>
              <VolumeControl video={el} muted={muted} onToggle={toggleMute} />
              {live ? (
                livePill
              ) : (
                <span className="px-1.5 text-[13px] font-bold whitespace-nowrap text-white/90 tabular-nums">
                  {clock(el.currentTime)}
                  <span className="text-white/50"> / {clock(duration)}</span>
                </span>
              )}
              <span className="flex-1" />
              <div className="relative">
                {settingsButton}
                {menuPanel}
              </div>
              {pipSupported && (
                <CtrlButton label={inPip ? 'Exit picture in picture (i)' : 'Picture in picture (i)'} onClick={togglePip} pressed={inPip}>
                  <PipIcon />
                </CtrlButton>
              )}
              {/* Video fed through Media Source (hls.js) can't be sent to a TV, so only Safari's own player offers it. */}
              {airplay && !engine.current.hls && (
                <CtrlButton label="AirPlay" onClick={() => (el as AppleVideo).webkitShowPlaybackTargetPicker?.()}>
                  <AirPlayIcon />
                </CtrlButton>
              )}
              {canFullscreen && (
                <CtrlButton label={fullscreen ? 'Exit full screen (f)' : 'Full screen (f)'} onClick={toggleFullscreen} tipAlign="right">
                  <FullscreenIcon exit={fullscreen} />
                </CtrlButton>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A control button: icon, accessible label, and a tooltip with its key on mouse devices. */
function CtrlButton({
  label,
  onClick,
  children,
  pressed,
  round = false,
  tip = true,
  tipAlign = 'center',
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  pressed?: boolean;
  round?: boolean;
  tip?: boolean;
  tipAlign?: 'center' | 'right';
}) {
  return (
    <button
      type="button"
      aria-label={label.replace(/ \(.\)$/, '')}
      aria-pressed={pressed}
      onClick={onClick}
      className={`group/btn relative inline-flex shrink-0 items-center justify-center text-white transition-colors focus-visible:outline-2 focus-visible:outline-race-300 ${round ? 'h-11 w-11 rounded-full bg-black/45' : 'h-10 w-10 rounded-lg hover:bg-white/10'} ${pressed ? 'text-race-300' : ''}`}
    >
      {children}
      {tip && (
        <span
          className={`pointer-events-none absolute bottom-full mb-2.5 rounded-md bg-navy-900 px-2 py-1 text-xs font-bold whitespace-nowrap text-white opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100 [@media(hover:none)]:hidden ${tipAlign === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}
          aria-hidden
        >
          {label}
        </span>
      )}
    </button>
  );
}

/** Sound on/off, with a slider that slides out on hover (mouse only: phones use their buttons). */
function VolumeControl({ video, muted, onToggle }: { video: HTMLVideoElement; muted: boolean; onToggle: () => void }) {
  const level = muted ? 'off' : video.volume < 0.5 ? 'low' : 'high';
  const value = muted ? 0 : video.volume;
  return (
    <div className="group/vol flex items-center">
      <CtrlButton label={muted ? 'Unmute (m)' : 'Mute (m)'} onClick={onToggle}>
        <VolumeIcon level={level} />
      </CtrlButton>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        aria-label="Volume"
        onChange={(e) => {
          video.volume = Number(e.target.value);
          video.muted = video.volume === 0;
        }}
        style={{ ['--fill' as string]: `${value * 100}%` }}
        className="player-range w-0 opacity-0 transition-[width,opacity,margin] duration-200 group-focus-within/vol:mr-2 group-focus-within/vol:w-[72px] group-focus-within/vol:opacity-100 group-hover/vol:mr-2 group-hover/vol:w-[72px] group-hover/vol:opacity-100 [@media(pointer:coarse)]:hidden"
      />
    </div>
  );
}

/** The replay's position: drag or tap anywhere on it; hover shows the time under the pointer. */
function SeekBar({ video, duration, onScrub }: { video: HTMLVideoElement; duration: number; onScrub: (on: boolean) => void }) {
  const bar = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const at = (clientX: number) => {
    const r = bar.current!.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };
  const played = drag ?? video.currentTime / duration;
  let loaded = 0;
  for (let i = 0; i < video.buffered.length; i++) if (video.buffered.start(i) <= video.currentTime + 0.5) loaded = Math.max(loaded, video.buffered.end(i) / duration);
  const tipAt = drag ?? hover;
  return (
    <div
      ref={bar}
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(video.currentTime)}
      aria-valuetext={`${clock(video.currentTime)} of ${clock(duration)}`}
      className="group/seek relative mx-2 flex h-6 cursor-pointer touch-none items-center outline-none"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag(at(e.clientX));
        onScrub(true);
      }}
      onPointerMove={(e) => {
        if (e.pointerType === 'mouse') setHover(at(e.clientX));
        if (drag !== null) setDrag(at(e.clientX));
      }}
      onPointerUp={(e) => {
        if (drag === null) return;
        video.currentTime = Math.min(at(e.clientX) * duration, duration - 0.1);
        setDrag(null);
        onScrub(false);
      }}
      onPointerCancel={() => {
        setDrag(null);
        onScrub(false);
      }}
      onPointerLeave={() => setHover(null)}
      onKeyDown={(e) => {
        if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          video.currentTime = e.key === 'Home' ? 0 : duration - 0.1;
        }
      }}
    >
      <div className={`relative w-full overflow-hidden rounded-full bg-white/25 transition-[height] duration-150 ${drag !== null ? 'h-1.5' : 'h-1 group-hover/seek:h-1.5'}`}>
        <div className="absolute inset-y-0 left-0 bg-white/35" style={{ width: `${Math.min(1, loaded) * 100}%` }} />
        <div className="absolute inset-y-0 left-0 bg-race" style={{ width: `${played * 100}%` }} />
      </div>
      {/* The handle leans like the logo's stripes. */}
      <div
        className={`absolute top-1/2 h-4 w-2.5 -translate-x-1/2 -translate-y-1/2 -skew-x-12 rounded-[2px] bg-race ring-2 ring-white transition-transform duration-150 ${drag !== null ? 'scale-110' : 'scale-0 group-hover/seek:scale-100 group-focus-visible/seek:scale-100 [@media(pointer:coarse)]:scale-100'}`}
        style={{ left: `${played * 100}%` }}
      />
      {tipAt !== null && (
        <span className="pointer-events-none absolute bottom-full mb-1.5 -translate-x-1/2 rounded-md bg-navy-900 px-2 py-1 text-xs font-bold text-white tabular-nums shadow-lg" style={{ left: `clamp(1.5rem, ${tipAt * 100}%, calc(100% - 1.5rem))` }}>
          {clock(tipAt * duration)}
        </span>
      )}
    </div>
  );
}

function SettingsMenu({
  view,
  setView,
  quality,
  onScreen,
  onQuality,
  rate,
  onRate,
  stats,
  onStats,
  maxHeight,
  className,
}: {
  view: MenuView;
  setView: (v: MenuView) => void;
  quality: QualityState | null;
  /** The picture on screen now, by its short side (0 before the first frame). */
  onScreen: number;
  onQuality: (c: QualityChoice) => void;
  /** Null for live streams (always 1×). */
  rate: number | null;
  onRate: (r: number) => void;
  stats: boolean;
  onStats: () => void;
  /** Fits inside the player (a phone's player is short). */
  maxHeight: number;
  className: string;
}) {
  // Each option says its own size (known once hls.js has loaded it); Auto says what's on screen.
  // Sizes only for what's on screen now: High is whatever the broadcaster sends, which changes
  // (a phone starts at 360p and steps up), so a size seen earlier can be out of date. Low is 360p.
  const highLabel = quality?.playing === 'hi' && onScreen ? `High · ${onScreen}p` : 'High';
  const lowLabel = quality?.playing === 'lo' && onScreen ? `Low · ${onScreen}p` : 'Low · 360p';
  const playingLabel = onScreen ? `${onScreen}p` : null;
  // High at 360p or less: the broadcaster's camera is sending that little (it climbs on a good connection).
  const highHint = quality?.playing === 'hi' && onScreen && onScreen <= 360 ? 'Stream is 360p now' : 'Best picture';
  const qualityValue = quality?.choice === 'high' ? highLabel : quality?.choice === 'low' ? lowLabel : `Auto${playingLabel ? ` · ${playingLabel}` : ''}`;
  const row = 'flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-bold text-white hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none';
  const back = (label: string) => (
    <button type="button" onClick={() => setView('main')} className={`${row} mb-1 border-b border-white/10`}>
      <ChevronIcon back />
      {label}
    </button>
  );
  const option = (selected: boolean, label: string, hint: string | null, onClick: () => void) => (
    <button key={label} type="button" role="menuitemradio" aria-checked={selected} onClick={onClick} className={row}>
      <span className={`w-[18px] ${selected ? 'text-race-300' : 'text-transparent'}`}>
        <CheckIcon />
      </span>
      <span className="flex-1">{label}</span>
      {hint && <span className="text-xs font-semibold text-white/55">{hint}</span>}
    </button>
  );
  return (
    <div
      role="menu"
      aria-label="Player settings"
      style={{ maxHeight }}
      className={`absolute z-10 w-64 overflow-y-auto rounded-xl bg-navy-900/95 py-1.5 shadow-2xl ring-1 ring-white/10 backdrop-blur-md ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {view === 'main' && (
        <>
          {quality && (
            <button type="button" role="menuitem" onClick={() => setView('quality')} className={row}>
              <span className="flex-1">Quality</span>
              <span className="text-[13px] font-semibold text-white/60">{qualityValue}</span>
              <ChevronIcon size={16} />
            </button>
          )}
          {rate !== null && (
            <button type="button" role="menuitem" onClick={() => setView('speed')} className={row}>
              <span className="flex-1">Speed</span>
              <span className="text-[13px] font-semibold text-white/60">{rate === 1 ? 'Normal' : `${rate}×`}</span>
              <ChevronIcon size={16} />
            </button>
          )}
          <button type="button" role="menuitemcheckbox" aria-checked={stats} onClick={onStats} className={row}>
            <span className="flex-1">Stream stats</span>
            <span className={`relative h-5 w-9 rounded-full transition-colors ${stats ? 'bg-race' : 'bg-white/25'}`} aria-hidden>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-[left] ${stats ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
          </button>
        </>
      )}
      {view === 'quality' && quality && (
        <>
          {back('Quality')}
          {option(quality.choice === 'auto', 'Auto', playingLabel ? `${playingLabel} now` : 'For your connection', () => onQuality('auto'))}
          {option(quality.choice === 'high', highLabel, highHint, () => onQuality('high'))}
          {option(quality.choice === 'low', lowLabel, 'Saves data', () => onQuality('low'))}
        </>
      )}
      {view === 'speed' && rate !== null && (
        <>
          {back('Speed')}
          {SPEEDS.map((s) => option(rate === s, s === 1 ? 'Normal' : `${s}×`, null, () => onRate(s)))}
        </>
      )}
    </div>
  );
}

/** What a key press or double tap just did, shown briefly over the picture. */
function FlashMark({ flash, onDone }: { flash: Flash; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 650);
    return () => clearTimeout(t);
  }, [onDone]);
  const place = flash.side === 'left' ? 'left-[12%]' : flash.side === 'right' ? 'right-[12%]' : 'left-1/2 -translate-x-1/2';
  return (
    <div className={`player-flash pointer-events-none absolute top-1/2 -translate-y-1/2 ${place}`} aria-hidden>
      <div className="flex min-w-20 flex-col items-center gap-1 rounded-2xl bg-navy-900/70 px-4 py-3 text-white">
        {flash.icon}
        {flash.text && <span className="text-base font-black italic">{flash.text}</span>}
      </div>
    </div>
  );
}

/** "Stream stats": what the player is doing, for curious viewers and support. */
function StatsPanel({ video, engine, quality, live, onClose, className }: { video: HTMLVideoElement; engine: PlayerEngine; quality: QualityState | null; live: boolean; onClose: () => void; className: string }) {
  const hls = engine.hls;
  const level = hls && hls.currentLevel >= 0 ? hls.levels[hls.currentLevel] : undefined;
  const frames = video.getVideoPlaybackQuality?.();
  const latency = Number(video.dataset.latency);
  const rows: [string, string][] = [
    ['Picture', video.videoWidth ? `${video.videoWidth}×${video.videoHeight}` : '—'],
    ['Quality', `${quality ? (quality.choice === 'auto' ? 'Auto' : quality.choice === 'high' ? 'High' : 'Low') : 'Single'}${quality?.playing ? ` (${quality.playing === 'hi' ? 'high' : 'low'})` : ''}`],
    ['Bitrate', level?.bitrate ? `${(level.bitrate / 1e6).toFixed(2)} Mbps` : '—'],
    ['Connection', hls?.bandwidthEstimate ? `${(hls.bandwidthEstimate / 1e6).toFixed(1)} Mbps` : '—'],
    ['Buffer', `${bufferedAhead(video).toFixed(1)} s`],
  ];
  if (live) rows.push(['Delay', Number.isFinite(latency) && latency > 0 ? `${latency.toFixed(1)} s behind the camera` : '—']);
  rows.push(['Dropped', frames ? `${frames.droppedVideoFrames} of ${frames.totalVideoFrames} frames` : '—']);
  rows.push(['Stalls', video.dataset.stalls ?? '0']);
  rows.push(['Player', hls ? `hls.js ${(hls.constructor as { version?: string }).version ?? ''}`.trim() : 'Safari HLS']);
  return (
    <div className={`absolute left-3 z-10 rounded-xl bg-black/75 py-2.5 pr-9 pl-3.5 font-mono text-[11px] leading-[18px] text-white shadow-lg sm:left-4 ${className}`} role="status" aria-label="Stream stats">
      <button type="button" aria-label="Close stats" onClick={onClose} className="absolute top-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white [&_svg]:h-4 [&_svg]:w-4">
        <CloseIcon />
      </button>
      <table>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td className="pr-3 text-white/55">{k}</td>
              <td className="tabular-nums">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
